import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "../lib/i18n";
import { langFromPath, LANG_PREFIX } from "../lib/seo";
import api from "../lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const USD_TO_AED = 3.67;
const DEFAULT_CATEGORIES = ["Supercar", "Luxury SUV", "Convertible", "Grand Tourer", "Sedan"];
const DEFAULT_DISCOUNTS = [{ min_days: 7, percent: 10 }, { min_days: 30, percent: 20 }];

export function AppProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(location.pathname);
  const [currency, setCurrency] = useState(localStorage.getItem("sd_currency") || "AED");
  const [settings, setSettings] = useState({
    hero_image: "",
    categories: DEFAULT_CATEGORIES,
    brands: [],
    models: {},
    trims: {},
    discounts: DEFAULT_DISCOUNTS,
    home_rows: {},
  });

  const refreshSettings = useCallback(() =>
    api.get("/settings")
      .then((r) => setSettings({
        hero_image: r.data.hero_image || "",
        categories: (r.data.categories && r.data.categories.length) ? r.data.categories : DEFAULT_CATEGORIES,
        brands: r.data.brands || [],
        models: r.data.models || {},
        trims: r.data.trims || {},
        discounts: r.data.discounts || [],
        home_rows: r.data.home_rows || {},
      }))
      .catch(() => {}), []);
  useEffect(() => { refreshSettings(); }, [refreshSettings]);

  const changeLang = useCallback(
    (nextLang) => navigate(LANG_PREFIX[nextLang] || "/"),
    [navigate],
  );
  const changeCurrency = useCallback((nextCurrency) => {
    setCurrency(nextCurrency);
    localStorage.setItem("sd_currency", nextCurrency);
  }, []);

  const discountFor = useCallback((days) => {
    let best = 0;
    for (const r of settings.discounts || []) {
      const md = parseInt(r.min_days) || 0, pc = parseFloat(r.percent) || 0;
      if (days >= md && pc > best) best = pc;
    }
    return best;
  }, [settings.discounts]);

  const t = translations[lang] || translations.EN;

  const price = useCallback((aed, usd) => {
    const val = currency === "AED" ? aed : (usd ?? aed / USD_TO_AED);
    const formatted = Math.round(val).toLocaleString("en-US");
    return `${currency} ${formatted}`;
  }, [currency]);

  const value = useMemo(() => ({
    lang,
    currency,
    changeLang,
    changeCurrency,
    t,
    price,
    categories: settings.categories,
    heroImage: settings.hero_image,
    brands: settings.brands,
    models: settings.models,
    trims: settings.trims,
    homeRows: settings.home_rows,
    discounts: settings.discounts,
    discountFor,
    refreshSettings,
  }), [
    lang,
    currency,
    changeLang,
    changeCurrency,
    t,
    price,
    settings,
    discountFor,
    refreshSettings,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
